import { NavLink, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, Boxes, LogOut, Network, Radio } from 'lucide-react'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useNotices } from '../hooks/useNotices'
import { logout } from '../auth/oidc'

/** Authenticated app shell: header + primary nav + routed content. */
export function AppShell() {
  const { t } = useTranslation()
  const noticeCount = useNotices().data?.length ?? 0

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2 rounded px-3 py-2 text-sm ${
      isActive ? 'bg-neutral-200 font-medium dark:bg-neutral-700' : 'text-neutral-600 dark:text-neutral-300'
    }`

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100">
      <header className="flex items-center justify-between border-b border-neutral-200 px-6 py-3 dark:border-neutral-800">
        <div>
          <h1 className="text-lg font-semibold">{t('app.name')}</h1>
          <p className="text-xs text-neutral-500">{t('app.tagline')}</p>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => void logout()}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-sm text-neutral-600 hover:bg-neutral-200 dark:text-neutral-300 dark:hover:bg-neutral-700"
          >
            <LogOut size={16} /> {t('auth.logout')}
          </button>
        </div>
      </header>
      <div className="flex">
        <nav className="flex w-48 flex-col gap-1 border-r border-neutral-200 p-3 dark:border-neutral-800">
          <NavLink to="/" className={linkClass} end>
            <Boxes size={16} /> {t('nav.directory')}
          </NavLink>
          <NavLink to="/explore" className={linkClass}>
            <Network size={16} /> {t('nav.explore')}
          </NavLink>
          <NavLink to="/sources" className={linkClass}>
            <Radio size={16} /> {t('nav.sources')}
          </NavLink>
          <NavLink to="/notices" className={linkClass}>
            <Bell size={16} /> {t('nav.notices')}
            {noticeCount > 0 && (
              <span className="ml-auto rounded-full bg-red-500 px-1.5 text-xs font-medium text-white">
                {noticeCount}
              </span>
            )}
          </NavLink>
        </nav>
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
